// Long-form guidance for each of the five RQUCL factors — used by the
// wizard's per-factor "Explain this" drawers (Sonia hifi scr-step1-drawer).
// Source: git show origin/seller:src/App.js lines 58..130.

export const factorGuides = {
  rarity: {
    title: 'Rarity — How Unique Is Your Patient Population?',
    what:
      'Rarity measures how hard it is for a buyer to find a comparable dataset elsewhere. A rare disease cohort or a unique population isolate is far more valuable than a common disease registry that many institutions can provide.',
    howToAssess:
      'Look up the prevalence of the primary condition in your cohort. If fewer than 10 comparable datasets exist worldwide, you are likely a 4 or 5. If your data covers a top-20 common disease, you are a 1 or 2.',
    buyerCare:
      'Pharma companies will pay a significant premium for data they cannot obtain from anyone else. Rare disease datasets are in especially high demand because clinical trials in these areas struggle to recruit.',
    benchmarks:
      'UK Biobank (general population): 1–2 | Pediatric leukemia cohort: 3 | ALS natural history with genomics: 4 | Progeria cohort (<500 patients worldwide): 5',
    scale: [
      'Very common (>5% prevalence). Widely available.',
      'Moderately common (1–5%). Available from registries.',
      'Uncommon (0.1–1%). Limited comparable datasets.',
      'Rare (1:10K–1:100K). Fewer than 10 comparable datasets globally.',
      'Ultra-rare (<1:100K) or first-of-kind. No comparable dataset exists.',
    ],
  },
  quality: {
    title: 'Quality — How Complete and Reliable Is Your Data?',
    what:
      'Quality captures data completeness, validation rigor, and technical standards. Buyers need to trust the data before they invest in analysis. Missing data, inconsistent formats, or unvalidated results significantly reduce value.',
    howToAssess:
      'Check your missing data rate, whether you have a QC pipeline, and if the data has been externally validated or generated by a certified lab (CAP/CLIA). If you have <5% missing data and standardized protocols, you are at least a 3.',
    buyerCare:
      'Low-quality data creates rework, delays regulatory submissions, and can invalidate study results. Buyers will deeply discount data they have to clean or re-validate.',
    benchmarks:
      'Consumer health app data: 1 | Single-site research DB: 2 | Multi-site clinical trial with standard CRFs: 3 | FinnGen: 4 | UK Biobank WGS (ISO 15189, <1% missing): 5',
    scale: [
      '>20% missing data. No QC pipeline. Self-reported, unverified.',
      '10–20% missing. Minimal validation. Inconsistent data entry.',
      '5–10% missing. Standardized protocols. Internal QC pipeline.',
      '1–5% missing. Externally validated. CAP/CLIA-certified.',
      '<1% missing. Audited. Clinical-grade (ISO 15189). FDA-compliant.',
    ],
  },
  usefulness: {
    title: 'Usefulness — How Applicable Is Your Data to R&D?',
    what:
      'Usefulness measures how directly the data can drive drug development, biomarker discovery, regulatory submissions, or AI model training. Data that is immediately actionable for clinical trials is worth far more than data useful only for academic publications.',
    howToAssess:
      'Ask: Can a pharma company use this data to identify drug targets, support an IND filing, or serve as an external control arm? If yes, you are a 4–5. If it is primarily useful for hypothesis generation, you are a 2–3.',
    buyerCare:
      'Pharma buyers evaluate data by how many steps it saves them in the drug development pipeline. Data supporting Phase II/III decisions or FDA submissions commands the highest premiums.',
    benchmarks:
      'Pilot study with no phenotype linkage: 1 | Retrospective chart review: 2 | Oncology cohort with treatment response + genomics: 3 | Multi-omics with 5-year outcomes: 4 | Dataset used in FDA-accepted RWE submission: 5',
    scale: [
      'Narrow academic interest. No therapeutic application.',
      'Limited clinical applicability. Hypothesis generation only.',
      'Moderate. Supports biomarker discovery in >=1 therapeutic area.',
      'Directly supports drug pipelines across multiple areas.',
      'Immediately actionable for trials, diagnostics, or AI at scale.',
    ],
  },
  compliance: {
    title: 'Compliance — How Ready Is Your Data for Commercial Use?',
    what:
      'Compliance reflects HIPAA readiness, de-identification status, consent scope, and regulatory audit trail. Even the best clinical data is worthless commercially if it cannot be legally shared with a partner.',
    howToAssess:
      'Has your data undergone HIPAA Safe Harbor or Expert Determination de-identification? Do you have broad commercial consent? Is there a Business Associate Agreement framework? If you need significant legal work before sharing, you are a 1–2.',
    buyerCare:
      'Compliance issues are deal-killers. A buyer will walk away from a dataset that requires re-consent or has ambiguous consent language, no matter how scientifically valuable it is. This is often the #1 bottleneck in deal timelines.',
    benchmarks:
      'Clinical notes with PHI intact: 1 | Academic dataset with internal-only IRB: 2 | Multi-site consortium with Safe Harbor: 3 | Health system with established commercial licensing: 4 | UK Biobank governance model: 5',
    scale: [
      'No HIPAA compliance. No consent for secondary use.',
      'Partial de-identification. Research-only consent.',
      'HIPAA Safe Harbor. General research consent. DUAs in place.',
      'HIPAA Expert Determination. Broad research + commercial consent.',
      'Full commercial + AI/ML consent. BAA-ready. GDPR-compliant.',
    ],
  },
  linkageRichness: {
    title: 'Linkage Richness — How Many Data Types Are Connected?',
    what:
      'This is often the most powerful value driver. Linkage Richness measures whether your genomic/molecular data is linked to clinical records, imaging, outcomes, and longitudinal follow-up. Linked data enables analyses that single-modality data cannot support.',
    howToAssess:
      'Count how many data modalities are linked at the patient level: genomics, EHR, imaging, outcomes, biospecimens, wearables. If you have genomics + EHR + outcomes + 5+ years of follow-up, you are a 4–5.',
    buyerCare:
      'Multi-modal linked data is the gold standard. A genome sequence alone has limited commercial value; the same genome linked to 10 years of clinical outcomes, treatment histories, and imaging data is transformative for drug discovery.',
    benchmarks:
      'Genotyping results with sample IDs only: 1 | Genotyping + age/sex/ICD codes: 2 | WGS + EHR + medications: 3 | FinnGen (genotyping + 40-year registry): 4 | UK Biobank (WGS + MRI + biochemistry + 15-year outcomes): 5',
    scale: [
      'Single data type, no linkage. No phenotype information.',
      'Single type + basic demographics and/or diagnosis codes.',
      'Primary type + linked EHR (diagnoses, meds, labs). >=2 modalities.',
      '3+ linked types (e.g., genomics + EHR + imaging). 3–5 yr longitudinal.',
      '4+ linked types + 5+ yr longitudinal + registries + re-contact.',
    ],
  },
};
